import { Injectable, inject } from '@angular/core';
import { StorageService } from '../../../@commons/@services/front/storage.service';
import { DayChoice, DayHistoryEntry } from './day-message.model';

/** Les messages montrés sur cet appareil, jour par jour. */
export const DAY_HISTORY_KEY = 'day-message-history';
/** Les messages qui ne se montrent qu'une fois sur l'appareil, par code. */
export const DAY_ONCE_KEY = 'day-message-once';
/** Assez pour la règle d'une semaine et les trois jours de suite, rien de plus. */
const KEEP_DAYS = 14;

/**
 * L'historique du mot du jour, par appareil (localStorage) : de quoi ne jamais
 * montrer le même message calme deux jours de suite, ni un code calme plus
 * d'une fois par semaine, et laisser un geste sans réponse partir dans la
 * quinzaine après trois jours. Sans stockage (navigation privée), l'historique
 * est vide : le moteur choisit comme au premier jour.
 */
@Injectable({ providedIn: 'root' })
export class DayMessageHistoryService {
  private readonly storage = inject(StorageService);

  entries(): DayHistoryEntry[] {
    try {
      const raw = this.storage.getItem(DAY_HISTORY_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /** Les messages « une fois » déjà montrés (par règle du catalogue). */
  shownOnce(): string[] {
    try {
      const parsed = JSON.parse(this.storage.getItem(DAY_ONCE_KEY) ?? '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private writeOnce(ids: string[]): void {
    try {
      this.storage.setItem(DAY_ONCE_KEY, JSON.stringify(ids));
    } catch {
      // Stockage refusé : le message pourra revenir, sans plus.
    }
  }

  /** Ce qui est montré aujourd'hui (message principal et autres gestes) remplace ce qui l'était déjà ce jour-là. */
  record(day: string, choice: DayChoice): void {
    const shown = [choice.head, ...choice.others]
      .filter((m) => !!m)
      .map((m, i): DayHistoryEntry => ({ day, key: m!.key, code: m!.code, plants: m!.plants.map((p) => p.uid ?? '').filter(Boolean), head: i === 0 }));
    const once = [choice.head, ...choice.others].filter((m) => !!m?.once).map((m) => m!.key.split(':')[0]);
    if (once.length) this.writeOnce([...new Set([...this.shownOnce(), ...once])]);
    const oldest = new Date(day);
    oldest.setDate(oldest.getDate() - KEEP_DAYS);
    const keep = this.entries().filter((e) => e.day !== day && e.day >= oldest.toISOString().slice(0, 10));
    try {
      this.storage.setItem(DAY_HISTORY_KEY, JSON.stringify([...keep, ...shown]));
    } catch {
      // Stockage plein ou refusé : l'historique reste ce qu'il était.
    }
  }
}
